import React from 'react';
import {
    Card,
    CardHeader,
    CardBody,
    Col,
    Row,
    DropdownMenu,
    DropdownItem,
    UncontrolledDropdown,
    DropdownToggle
} from 'reactstrap';
import AIGStatusBadge from '../../shared/StatusBadge/StatusBadge';
import styles from './MyPlans.module.css';
import classNames from 'classnames';
import moment from 'moment';
import { ReactComponent as ACSystemIcon } from '../../../assets/images/air-conditioner.svg';
import { ReactComponent as ElectricalSystem } from '../../../assets/images/electrical-system.svg';
import { ReactComponent as SurgeProtectionIcon } from '../../../assets/images/Vendor/ET/electric-surge.svg';
import { ReactComponent as VehicleChargerIcon } from '../../../assets/images/Vendor/ET/vehicle-charger.svg';
import { ReactComponent as ApplestandalonePhoneIcon } from '../../../assets/images/Vendor/Applestandalone/phone.svg';
import { useEffect, useState, useContext } from 'react';
import { ReactComponent as ToggleIcon } from '../../../assets/images/toggle.svg';
import { ContractContext } from '../../contexts/ContractContext';
import getClientContent from '../../_BrandingProvider/brandContent.jsx';

export const PlanCard = ({ contract, ...props }) => {
    const [contractDetails, setContractDetails] = useState({});
    const context = useContext(ContractContext);
    const clientId = context.data?.clientId || context.data?.contract?.clientId || 'aig';
    const menuItems =
        getClientContent(clientId)?.variableText?.menuItems || getClientContent('aig')?.variableText?.menuItems;
    const sections =
        getClientContent(clientId)?.variableText?.sections || getClientContent('aig')?.variableText?.sections;

    useEffect(() => {
        getContractDetails();
    }, [contract]);

    const paymentOptionLabel = (id) => {
        const contractTerms = props?.contractTerms?.find((x) => x.termId == id);
        const label = contractTerms
            ? contractTerms?.termName?.trim().includes('One Month')
                ? 'Monthly'
                : contractTerms?.termName?.trim()
            : '';
        return label;
    };

    const getContractDetails = () => {
        const asset = contract.assets[0] ?? null;
        let summary = null;

        if (asset) {
            const contractAsset = contract.contractAssetModel.find((ca) => ca.assetId == asset.assetId);
            const psDate = moment(contractAsset?.contractAssetCoverageModel[0]?.startDate).format('MM/DD/YYYY');
            const waitPeriod = contract.riskProfile?.waitPeriod ?? 0;
            const wpEnds =
                waitPeriod > 0 ? moment(psDate, 'MM/DD/YYYY').add(waitPeriod, 'days').format('MM/DD/YYYY') : '';

            summary = {
                assetId: asset.assetId,
                assetName: asset.assetName?.replace(/\s/g, '').toLowerCase(),
                assetTypeId: asset.assetTypeId,
                assetIndustryId: asset.assetIndustryId,
                assetDisplayCategory: asset.assetDisplayCategory,
                removePurchaseDate: asset.removePurchaseDate,
                planStartDate: psDate,
                contractNumber: contract.contractNumber,
                paymentOption: paymentOptionLabel(contractAsset?.offer?.riskProfileModel?.termId ?? 0),
                contractStatus: contract.statusModel.statusName ?? '',
                statusId: contract?.statusId,
                contractAssetId: contractAsset?.contractAssetId,
                waitPeriodEnds: wpEnds,
                purchaseDate: contract.purchaseDate,
                riskProfileId: contractAsset?.offer?.riskProfileId,
                contract: contract
            };
        }

        setContractDetails(summary);
    };

    const toggleDropdown = () => {
        return (
            <UncontrolledDropdown
                direction='right'
                position='top'
            >
                <DropdownToggle
                    tag='span'
                    className={styles.toggleContainer}
                >
                    <ToggleIcon
                        className={styles.toggleIcon}
                        alt='toggle dropdown'
                    />
                </DropdownToggle>
                <DropdownMenu>
                    {menuItems?.viewPlanDetails && (
                        <DropdownItem
                            onClick={() =>
                                contractDetails
                                    ? props?.handleSelectedActionClick('ViewPlanDetails', contractDetails)
                                    : ''
                            }
                        >
                            View Plan Details
                        </DropdownItem>
                    )}
                    {menuItems?.coverageDetails && (
                        <DropdownItem
                            onClick={() =>
                                contractDetails
                                    ? props?.handleSelectedActionClick('CoverageDetails', contractDetails)
                                    : ''
                            }
                        >
                            Coverage Details
                        </DropdownItem>
                    )}
                    {menuItems?.reprintContract && (
                        <DropdownItem onClick={() => props?.handleSelectedActionClick('ReprintContract')}>
                            Reprint Contract
                        </DropdownItem>
                    )}
                    {menuItems?.termsAndConditions && (
                        <DropdownItem
                            onClick={() =>
                                contractDetails
                                    ? props?.handleSelectedActionClick('TermsAndConditions', contractDetails)
                                    : ''
                            }
                        >
                            Terms and Conditions
                        </DropdownItem>
                    )}
                    {menuItems?.cancelContract && (
                        <DropdownItem
                            disabled={!contract?.cancelEnabled}
                            onClick={() => props?.handleSelectedActionClick('CancelContract')}
                        >
                            Cancel Contract
                        </DropdownItem>
                    )}
                </DropdownMenu>
            </UncontrolledDropdown>
        );
    };

    return (
        <Card className={classNames(styles.planCard, 'shadow', 'mb-4')}>
            <CardHeader className={styles.cardHeader}>
                <Row className='flex-nowrap'>
                    <Col
                        xs='11'
                        md='10'
                        lg='11'
                    >
                        <h6 className='mb-1'>{contract?.riskProfile?.name}</h6>
                        <span className='d-sm-none'>
                            <AIGStatusBadge
                                statusId={contractDetails?.statusId}
                                statusType='contract'
                            />
                        </span>
                        <div className='d-none d-sm-block'>
                            <AIGStatusBadge
                                statusId={contractDetails?.statusId}
                                statusType='contract'
                            />
                        </div>
                    </Col>
                    {/* DESKTOP/TABLET VIEW: hide for small devices */}
                    <Col
                        md='2'
                        lg='1'
                        className='d-none d-sm-block'
                    >
                        {toggleDropdown()}
                    </Col>
                    {/* MOBILE VIEW: hide for large devices */}
                    <Col
                        xs='1'
                        className='d-sm-none pl-0 pr-0'
                    >
                        {toggleDropdown()}
                    </Col>
                </Row>
            </CardHeader>
            <CardBody className={styles.cardBody}>
                <Row>
                    <Col
                        xs='6'
                        md='8'
                        lg='8'
                    >
                        <ul className={classNames(styles.listDetails, 'noBullets')}>
                            <li>
                                Plan Start:{' '}
                                <strong>{moment(contractDetails?.planStartDate).format('MM/DD/YYYY')}</strong>
                            </li>
                            <li>Contract: #{contractDetails?.contractNumber}</li>
                            <li>
                                {contract?.riskProfile?.termModel?.recurring ? 'Payment' : 'Term'}:{' '}
                                <strong>{contractDetails?.paymentOption}</strong>
                            </li>
                        </ul>
                    </Col>
                    <Col
                        xs='6'
                        md='4'
                        lg='4'
                    >
                        {contractDetails?.assetName?.replace(/\s/g, '').toLowerCase() === 'a/csystem' && (
                            <ACSystemIcon className={styles.assetIcon} />
                        )}
                        {contractDetails?.assetName?.replace(/\s/g, '').toLowerCase() === 'electricvehiclecharger' && (
                            <VehicleChargerIcon className={styles.assetIcon} />
                        )}
                        {contractDetails?.assetName?.replace(/\s/g, '').toLowerCase() === 'electricalsystems'
                            && !contractDetails?.assetName.includes('Vehicle') && (
                                <ElectricalSystem className={styles.assetIcon} />
                            )}
                        {(contractDetails?.assetName?.replace(/\s/g, '').toLowerCase() === 'surge'
                            || contractDetails?.assetName?.replace(/\s/g, '').toLowerCase() === 'surgeprotection'
                            || contractDetails?.assetName?.replace(/\s/g, '').toLowerCase() === 'dwelling') && (
                            <SurgeProtectionIcon className={styles.assetIcon} />
                        )}
                    </Col>
                </Row>
            </CardBody>
        </Card>
    );
};
